/**
 * autoLanguage.js
 * Handles fetching and caching translations for non-vi/en languages
 * using the MyMemory API (free, no auth required).
 */
import i18n from './index';
import en from './locales/en.json';

const CACHE_PREFIX = 'i18n_auto_v1_';
const BATCH_SIZE = 5; // strings per API call

// ── Flatten / unflatten nested JSON ──────────────────────────────────────────

function flattenJson(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenJson(value, fullKey));
    } else {
      result[fullKey] = value;
    }
  }
  return result;
}

function unflattenJson(flat) {
  const result = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let cur = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!cur[parts[i]]) cur[parts[i]] = {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
  }
  return result;
}

// ── Single string translation ─────────────────────────────────────────────────

async function translateOne(text, targetLang) {
  const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
  const data = await res.json();
  if (data?.responseStatus === 200 && data?.responseData?.translatedText) {
    return data.responseData.translatedText;
  }
  return text;
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Load auto-translated resources for a given language code.
 * - Checks localStorage cache first (permanent cache per language)
 * - If not cached: fetches translations from MyMemory API
 * - Adds the translated bundle to i18next
 *
 * @param {string} langCode   - e.g. 'fr', 'ja', 'ko'
 * @param {function} onProgress - (percent: 0-100) callback
 */
export async function loadAutoLanguage(langCode, onProgress) {
  if (langCode === 'vi' || langCode === 'en') return;

  // 1. Check cache
  const cacheKey = `${CACHE_PREFIX}${langCode}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const translations = JSON.parse(cached);
      i18n.addResourceBundle(langCode, 'translation', translations, true, true);
      if (onProgress) onProgress(100);
      return;
    } catch { /* corrupted cache, re-translate */ }
  }

  // 2. Flatten English strings
  const flatEn = flattenJson(en);
  const entries = Object.entries(flatEn).filter(([, v]) => typeof v === 'string');
  const flatTranslated = {};

  // 3. Translate in batches
  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const batch = entries.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async ([key, value]) => {
        // Skip pure-placeholder strings like "{{count}} tiết"
        const stripped = value.replace(/\{\{[^}]+\}\}/g, '').trim();
        if (!stripped) {
          flatTranslated[key] = value;
          return;
        }
        try {
          flatTranslated[key] = await translateOne(value, langCode);
        } catch {
          flatTranslated[key] = value; // fallback to English on error
        }
      })
    );
    if (onProgress) {
      onProgress(Math.min(99, Math.round((i + BATCH_SIZE) / entries.length * 100)));
    }
  }

  // 4. Store in i18next and cache
  const nested = unflattenJson(flatTranslated);
  try {
    localStorage.setItem(cacheKey, JSON.stringify(nested));
  } catch { /* localStorage full, skip caching */ }

  i18n.addResourceBundle(langCode, 'translation', nested, true, true);
  if (onProgress) onProgress(100);
}

/** Remove cached translations for a language (forces re-fetch) */
export function clearLanguageCache(langCode) {
  localStorage.removeItem(`${CACHE_PREFIX}${langCode}`);
}

/** List of popular languages for the language switcher */
export const POPULAR_LANGUAGES = [
  { code: 'fr', name: 'Français',          flag: '🇫🇷', countryCode: 'fr' },
  { code: 'zh', name: '中文',               flag: '🇨🇳', countryCode: 'cn' },
  { code: 'ja', name: '日本語',             flag: '🇯🇵', countryCode: 'jp' },
  { code: 'ko', name: '한국어',             flag: '🇰🇷', countryCode: 'kr' },
  { code: 'th', name: 'ภาษาไทย',           flag: '🇹🇭', countryCode: 'th' },
  { code: 'id', name: 'Bahasa Indonesia',   flag: '🇮🇩', countryCode: 'id' },
  { code: 'es', name: 'Español',            flag: '🇪🇸', countryCode: 'es' },
  { code: 'de', name: 'Deutsch',            flag: '🇩🇪', countryCode: 'de' },
  { code: 'ru', name: 'Русский',            flag: '🇷🇺', countryCode: 'ru' },
  { code: 'ar', name: 'العربية',            flag: '🇸🇦', countryCode: 'sa' },
];
